/**
 * Authentication hook
 * Manages auth state and provides authentication functions
 */

import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import type { UserProfile } from '../../../shared/types';
import { auth } from '../config/firebase';
import { getUserProfile } from '../services/user.service';
import { getCompanyById } from '../services/company.service';
import { signOut as signOutService } from '../services/auth.service';

type AuthenticatedUser = UserProfile & { companyName?: string; companyTimezone?: string; gracePeriodMinutes?: number };

const buildAuthenticatedUser = async (profile: UserProfile | null): Promise<AuthenticatedUser | null> => {
  if (!profile?.userType || !['super_admin', 'company', 'manager'].includes(profile.userType)) {
    return null;
  }

  if (profile.userType !== 'super_admin' && profile.companyId) {
    try {
      const company = await getCompanyById(profile.companyId);
      return {
        ...profile,
        companyName: company?.name,
        companyTimezone: company?.settings?.timezone,
        gracePeriodMinutes: company?.settings?.gracePeriodMinutes,
      };
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  }

  return profile;
};

export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const hydrateUser = async (user: any) => {
      setLoading(true);
      if (user) {
        try {
          // Force token refresh so custom claims set after account creation are picked up
          await user.getIdToken(true);
          const profile = await getUserProfile(user.uid);
          const hydrated = await buildAuthenticatedUser(profile);
          if (hydrated) {
            setCurrentUser(hydrated);
          } else {
            await signOutService();
            setCurrentUser(null);
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      void hydrateUser(user);
    });

    return () => unsubscribe();
  }, []);

  const signOut = async () => {
    await signOutService();
    setCurrentUser(null);
  };

  const refreshUser = async () => {
    const user = auth.currentUser;
    if (user) {
      const profile = await getUserProfile(user.uid);
      const hydrated = await buildAuthenticatedUser(profile);
      setCurrentUser(hydrated);
    }
  };

  return {
    currentUser,
    loading,
    signOut,
    refreshUser,
  };
};
